/**
 * Estructura estándar para un equipo de red procesado
 */
export interface NetworkDevice {
    ip: string;
    name: string;
    tipo: "PMPAP" | "PMPSM" | "RAJANT" | string;
    position: [number, number];
    status: string; // 'OK' | 'ALERT' | 'ALARM' | 'DOWN'
    padreIp?: string; // Para dibujar enlaces con el nodo padre
}

/**
 * Estructura de un Marcador Agrupado en el mapa (Cluster)
 */
export interface ClusteredMarker {
    id: string;
    position: [number, number];
    devices: NetworkDevice[];
    compositeImage: string;
}

/**
 * Distancia euclidiana entre coordenadas
 */
export function getGeoDistance(pos1: [number, number], pos2: [number, number]): number {
    if (!pos1 || !pos2) return 999;
    const dx = pos1[0] - pos2[0];
    const dy = pos1[1] - pos2[1];
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Agrupa dispositivos visibles si la distancia entre ellos es menor al umbral (threshold).
 * Asigna la imagen según la combinación exacta de tipos presentes en el grupo.
 */
export function groupVisibleDevices(
    devices: NetworkDevice[],
    threshold: number,
    constantImages: Record<string, any>
): ClusteredMarker[] {
    const clusters: ClusteredMarker[] = [];
    const visited = new Set<string>();

    devices.forEach((device) => {
        if (visited.has(device.ip)) return;

        // Buscar dispositivos cercanos al punto actual
        const group: NetworkDevice[] = [device];
        visited.add(device.ip);

        devices.forEach((other) => {
            if (!visited.has(other.ip)) {
                const dist = getGeoDistance(device.position, other.position);
                if (dist <= threshold) {
                    group.push(other);
                    visited.add(other.ip);
                }
            }
        });

        // Detectar qué tipos de equipos componen este grupo
        const types = new Set(group.map((d) => d.tipo.toUpperCase()));
        const hasAP = types.has("PMPAP");
        const hasSM = types.has("PMPSM");
        const hasRAJ = types.has("RAJANT");

        // Selección de imagen compuesta según el trío, dúo o ícono individual
        let compositeImage = constantImages.IMAGES.onlysolar;

        if (hasAP && hasSM && hasRAJ) {
            compositeImage = constantImages.IMAGES.apsolar; // Combinado Triple
        } else if (hasAP && hasRAJ) {
            compositeImage = constantImages.IMAGES.apsolar; // AP + Rajant
        } else if (hasSM && hasRAJ) {
            compositeImage = constantImages.IMAGES.solarsm; // SM + Rajant
        } else if (hasAP && hasSM) {
            compositeImage = constantImages.IMAGES.onlyap; // AP + SM
        } else if (hasAP) {
            compositeImage = constantImages.IMAGES.onlyap;
        } else if (hasSM) {
            compositeImage = constantImages.IMAGES.onlypmp;
        } else if (hasRAJ) {
            compositeImage = constantImages.IMAGES.onlysolar;
        }

        // Coordenada media para centrar la marca en el grupo
        const avgLat = group.reduce((acc, d) => acc + d.position[0], 0) / group.length;
        const avgLng = group.reduce((acc, d) => acc + d.position[1], 0) / group.length;

        clusters.push({
            id: group.map((d) => d.ip).join("_"),
            position: [avgLat, avgLng],
            devices: group,
            compositeImage,
        });
    });

    return clusters;
}

/**
 * Coordenadas [0,0] significan que el equipo no tiene GPS resuelto: no se dibuja línea.
 */
const isValidPosition = (position: [number, number]) =>
  position[0] !== 0 && position[1] !== 0;

/**
 * "Con problema" es todo lo que no sea OK (ALERT, ALARM o DOWN). El status puede
 * llegar en minúsculas desde algunas APIs, por eso se normaliza antes de comparar.
 * Es la misma regla que filtra los marcadores del mapa, así se evita que un equipo
 * se pinte sin su enlace o viceversa.
 */
export function hasProblem(status: string): boolean {
    return (status ?? "").toUpperCase() !== "OK";
}

/**
 * Genera la lista de polilíneas (enlaces) uniendo únicamente equipos visibles.
 * Soporta nomenclaturas ip_fuente/ip_cliente y sourceIp/targetIp.
 *
 * `requireBothEnds` (default true) reproduce el comportamiento original: la línea
 * solo se dibuja si AMBOS extremos están visibles. Con false basta con que uno esté
 * visible, y el extremo oculto se resuelve desde `allDevices`. Eso hace falta cuando
 * el mapa filtra por estado: un equipo con problema cuelga de uno sano que no se está
 * pintando, y sin esta tolerancia quedaría flotando sin contexto de red.
 *
 * `requireProblemEnd` (default false) restringe el dibujo a los enlaces que tienen al
 * menos un extremo VISIBLE con problema: es lo que usa DEMO en el filtro "Solo
 * Problemas" para mostrar únicamente las líneas de los equipos con problema, y no
 * también las de los sanos (que colgarían de los AP, que siempre están visibles). Se
 * evalúa sobre el extremo visible para que desmarcar el checkbox de un tipo siga
 * ocultando sus enlaces.
 */
export function buildActiveLinks(
    visibleDevices: NetworkDevice[],
    rawPolylines: Array<any>,
    options: { requireBothEnds?: boolean; allDevices?: NetworkDevice[]; requireProblemEnd?: boolean } = {}
) {
    const requireBothEnds = options.requireBothEnds !== false;
    const requireProblemEnd = options.requireProblemEnd === true;

    // Mapa de acceso rápido indexado por IP para los dispositivos actualmente visibles
    const deviceMap = new Map<string, NetworkDevice>();
    visibleDevices.forEach((dev) => deviceMap.set(dev.ip, dev));

    // Catálogo completo, solo para resolver el extremo oculto en el modo tolerante.
    const allMap = new Map<string, NetworkDevice>();
    if (!requireBothEnds && options.allDevices) {
        options.allDevices.forEach((dev) => allMap.set(dev.ip, dev));
    }

    const activePolylines: Array<{ path: [number, number][]; colorLine: string }> = [];

    rawPolylines.forEach((link) => {
        // Tolerancia a múltiples nombres de propiedades provenientes de la API
        const sourceIp = link.ip_fuente || link.sourceIp || link.ip_padre;
        const targetIp = link.ip_cliente || link.targetIp || link.ip_hijo;

        const visibleSource = deviceMap.get(sourceIp);
        const visibleTarget = deviceMap.get(targetIp);

        // Filtro de estado: solo interesan las líneas de los equipos con problema.
        // Se mira el extremo VISIBLE para que un tipo apagado por checkbox no deje
        // enlaces sueltos en el mapa.
        if (
            requireProblemEnd &&
            !(visibleSource && hasProblem(visibleSource.status)) &&
            !(visibleTarget && hasProblem(visibleTarget.status))
        ) {
            return;
        }

        // Comportamiento original: la línea existe solo si AMBOS extremos están visibles.
        if (requireBothEnds) {
            if (!visibleSource || !visibleTarget) return;

            if (!isValidPosition(visibleSource.position) || !isValidPosition(visibleTarget.position)) return;

            const color = link.colorLine || (visibleTarget.status.toUpperCase() === 'OK' ? '#00FF00' : '#FF0000');

            activePolylines.push({
                path: [visibleSource.position, visibleTarget.position],
                colorLine: color,
            });
            return;
        }

        // Modo tolerante: basta con que un extremo esté visible.
        if (!visibleSource && !visibleTarget) return;

        const sourceEnd = visibleSource ?? allMap.get(sourceIp);
        const targetEnd = visibleTarget ?? allMap.get(targetIp);
        if (!sourceEnd || !targetEnd) return;

        if (!isValidPosition(sourceEnd.position) || !isValidPosition(targetEnd.position)) return;

        // El color sigue el estado del extremo visible; si el cliente está oculto, se usa el padre.
        // targetEnd ya esta garantizado por el guard anterior.
        const reference = visibleTarget ?? visibleSource ?? targetEnd;
        const color = link.colorLine || (reference.status.toUpperCase() === 'OK' ? '#00FF00' : '#FF0000');

        activePolylines.push({
            path: [sourceEnd.position, targetEnd.position],
            colorLine: color,
        });
    });

    return activePolylines;
}